/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';

import { EntitiesSlice } from '../../../../ts/state';
import { FETCH_SYSTEM_ENTITIES_SUCCESS } from '../system-entities/actionTypes';
import * as types from './actionTypes';

// Array of entity IDs
const userIds = (
  state: EntitiesSlice['userIds'] = [],
  action: AnyAction
): EntitiesSlice['userIds'] => {
  switch (action.type) {
    case types.FETCH_ENTITIES_SUCCESS: // OVERWRITE existing user IDs
    case types.FILTER_ENTITIES_SUCCESS:
    case types.IMPORT_ENTITIES_SUCCESS:
    case types.REPLACE_ENTITIES_SUCCESS:
      return action.response.result.filter(id => !id.startsWith('sys-'));
    case types.FETCH_MORE_ENTITIES_SUCCESS: // APPEND the new user ids, we've already filtered out sys-* in the api call
      return state.concat(action.response.result);
    case types.CREATE_ENTITY_SUCCESS: // APPEND TO existing user IDs
      return [...state, action.response.result];
    case types.UPDATE_ENTITY_SUCCESS: // REPLACE ONE user ID
      if (action.previousEntityId) {
        return [
          ...state.filter(id => id !== action.previousEntityId),
          action.response.result
        ].sort();
      }
      return state; // lint sas "no" to fallthrough
    case types.DELETE_ENTITY_SUCCESS: // DELETE specified user ID
      return state.filter(entity => entity !== action.entity.entity);
    case types.DELETE_ENTITIES_SUCCESS:
      // action.entities are the entities we deleted. here we are removing them from the list of userIds
      return state.filter(entity => !action.entities.includes(entity));
    case types.DELETE_ALL_ENTITIES_SUCCESS:
      state = [];
      return state;
    default:
      return state;
  }
};

const sysIds = (
  state: EntitiesSlice['sysIds'] = [],
  action: AnyAction
): EntitiesSlice['sysIds'] => {
  switch (action.type) {
    case types.CREATE_ENTITY_SUCCESS:
      if (
        action.response.result.startsWith('sys-') &&
        !state.includes(action.response.result)
      ) {
        return [...state, action.response.result];
      }
      return [...state];
    case types.FILTER_ENTITIES_SUCCESS:
    case FETCH_SYSTEM_ENTITIES_SUCCESS:
      return action.response.result.filter(id => id.startsWith('sys-'));
    case types.DELETE_ENTITY_SUCCESS: // DELETE specified sys ID
      return [...state];
    default:
      return state;
  }
};

const INITIAL_PROPS: EntitiesSlice['props'] = {
  lastFetched: 0,
  loading: false,
  loadingMore: false,
  errorMessage: null
};

// UI specific information
const props = (
  state: EntitiesSlice['props'] = INITIAL_PROPS,
  action
): EntitiesSlice['props'] => {
  switch (action.type) {
    case types.FETCH_ENTITIES_START:
    case types.FILTER_ENTITIES_START:
    case types.CREATE_VALUE_START:
    case types.UPDATE_ENTITY_START:
    case types.UPDATE_VALUE_START:
    case types.DELETE_ENTITY_VALUES_START:
      return { ...state, loading: true, errorMessage: null };
    case types.FETCH_MORE_ENTITIES_START:
      return { ...state, loadingMore: true, errorMessage: null };
    case types.DELETE_ENTITY_START:
    case types.DELETE_ENTITIES_START:
    case types.DELETE_ALL_ENTITIES_START:
    case types.REPLACE_ENTITIES_START:
      return { ...state, deleting: true, errorMessage: null };
    case types.CREATE_ENTITY_START:
      return { ...state, creating: true, errorMessage: null };
    case types.FETCH_ENTITIES_SUCCESS:
      return { ...state, loading: false, lastFetched: Date.now() };
    case types.FILTER_ENTITIES_SUCCESS:
    case types.UPDATE_ENTITY_SUCCESS:
    case types.UPDATE_VALUE_SUCCESS:
    case types.CREATE_VALUE_SUCCESS:
    case types.DELETE_ENTITY_VALUES_SUCCESS:
      return { ...state, loading: false };
    case types.FETCH_MORE_ENTITIES_SUCCESS:
      return { ...state, loadingMore: false };
    case types.CREATE_ENTITY_SUCCESS:
      return { ...state, creating: false };
    case types.DELETE_ENTITY_SUCCESS:
    case types.DELETE_ENTITIES_SUCCESS:
    case types.DELETE_ALL_ENTITIES_SUCCESS:
    case types.REPLACE_ENTITIES_SUCCESS:
      return { ...state, deleting: false };
    case types.FETCH_ENTITIES_ERROR:
    case types.FILTER_ENTITIES_ERROR:
    case types.FETCH_MORE_ENTITIES_ERROR:
    case types.CREATE_ENTITY_ERROR:
    case types.CREATE_VALUE_ERROR:
    case types.UPDATE_ENTITY_ERROR:
    case types.UPDATE_VALUE_ERROR:
    case types.DELETE_ENTITY_ERROR:
    case types.DELETE_ENTITIES_ERROR:
    case types.IMPORT_ENTITIES_ERROR:
    case types.DELETE_ENTITY_VALUES_ERROR:
    case types.DELETE_ALL_ENTITIES_ERROR:
    case types.REPLACE_ENTITIES_ERROR:
      return {
        ...state,
        loading: false,
        loadingMore: false,
        deleting: false,
        creating: false,
        errorMessage: action.error.error,
        action: action
      };
    default:
      return state;
  }
};

export const reducer = combineReducers({ userIds, sysIds, props });

export default reducer;
